import React, { useState, useEffect } from 'react';
import { ExternalLink, AlertTriangle, Activity, Globe, Lock, BarChart, Zap, Server, Eye, Bell, Terminal, LineChart, Cpu, Layers, ChevronRight, ShieldAlert, Network, Bot, X, Scan, Search, FolderOpen, ShieldCheck } from 'lucide-react';

function Dashboard() {
  // Real-time state management for various dashboard components
  const [pulseRate, setPulseRate] = useState(1000);
  const [globalThreatLevel, setGlobalThreatLevel] = useState(75);
  const [threatStatus, setThreatStatus] = useState('ELEVATED');
  const [showAIModal, setShowAIModal] = useState(false);
  const [selectedAIRegion, setSelectedAIRegion] = useState<string | null>(null);
  const [aiChatbotUrl, setAiChatbotUrl] = useState<string | null>(null);
  const [vulnerabilityFindings, setVulnerabilityFindings] = useState(847);
  
  // Update real-time data at intervals
  useEffect(() => {
    // Update global threat level
    const threatInterval = setInterval(() => {
      const newThreatLevel = Math.floor(65 + Math.random() * 25);
      setGlobalThreatLevel(newThreatLevel);

      if (newThreatLevel > 85) {
        setThreatStatus('CRITICAL');
      } else if (newThreatLevel > 70) {
        setThreatStatus('ELEVATED');
      } else {
        setThreatStatus('MODERATE');
      }
    }, 5000);

    // Simulating changing threat levels
    const pulseInterval = setInterval(() => {
      setPulseRate(prev => prev > 800 ? prev - 100 : 1000);
    }, 5000);

    // Update vulnerability findings randomly
    const vulnerabilityInterval = setInterval(() => {
      setVulnerabilityFindings(prev => {
        const change = Math.floor(Math.random() * 10) - 5;
        return Math.max(800, Math.min(900, prev + change));
      });
    }, 3000);

    // Clean up all intervals on component unmount
    return () => {
      clearInterval(threatInterval);
      clearInterval(pulseInterval);
      clearInterval(vulnerabilityInterval);
    };
  }, []);

  // Random data for visualization
  const generateData = () => {
    return Array.from({ length: 48 }).map(() => Math.floor(15 + Math.random() * 85));
  };

  const [activityData] = useState(generateData());

  // Handle AI Chatbot region selection
  const handleAIRegionSelect = (region: 'US' | 'PK') => {
    setSelectedAIRegion(region);
    if (region === 'US') {
      // Open in new window/tab since iframe embedding might be blocked
      window.open('https://demo-ai-us.alertstrike.com', '_blank');
    } else {
      // Open in new window/tab since iframe embedding might be blocked
      window.open('https://demo-ai-pk.alertstrike.com', '_blank');
    }
    // Close the modal after opening the URL
    setShowAIModal(false);
    setSelectedAIRegion(null);
  };

  // Close AI chatbot
  const closeAIChatbot = () => {
    setShowAIModal(false);
    setSelectedAIRegion(null);
  };

  return (
    <main className="container mx-auto px-3 sm:px-6 py-4 sm:py-8 relative">
      {/* HUD-Style Top Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-10 p-1 border border-red-900/30 rounded-md bg-black/30 backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-2 px-2 py-2 sm:py-0 sm:gap-4 sm:px-4">
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">SYS:</span> ACTIVE
          </div>
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">PROT:</span> MAXIMAL
          </div>
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">SEC:</span> LEVEL 4
          </div>
        </div>
        <div className="flex items-center px-2 py-2 sm:py-0">
          <div className="h-2 bg-gradient-to-r from-red-900 via-red-600 to-red-900 animate-pulse mx-2 sm:mx-4" style={{ width: '60px', maxWidth: '25vw' }}></div>
          <div className="text-red-400 font-mono text-xs px-2 sm:px-4">
            <span className="text-slate-500">TIME:</span> {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Dashboard Header */}
      <div className="mb-8 sm:mb-12 relative">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <div className="relative">
              <h1 className="text-2xl sm:text-4xl font-bold mb-1">
                AlertStrike <span className="text-red-500">SIEM & XDR Solution</span>
              </h1>
              <div className="absolute -top-2 -right-2 w-2 h-2 bg-red-600 rounded-full animate-ping"></div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-red-400 text-xs sm:text-base mb-4 sm:mb-6">
              <ShieldAlert className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Real-time Security</span>
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 hidden sm:block"></span>
              <span className="hidden sm:inline">Advanced Threat Detection</span>
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 hidden sm:block"></span>
              <span className="hidden sm:inline">AI-Powered Response</span>
            </div>
          </div>
          
          <div className="bg-black/40 rounded-md border border-red-900/30 p-3 sm:p-4 backdrop-blur-sm w-full sm:w-auto">
            <div className="flex items-center space-x-4">
              <div>
                <div className="text-xs sm:text-sm text-slate-400 mb-1">Global Threat Level</div>
                <div className="text-xl sm:text-2xl font-bold text-red-500">{threatStatus}</div>
              </div>
              <div className="relative h-14 w-14 sm:h-16 sm:w-16">
                <svg className="w-full h-full" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="16" fill="none" stroke="#27272a" strokeWidth="2"></circle>
                  <circle
                    cx="18" cy="18" r="16"
                    fill="none" stroke="#dc2626" strokeWidth="2"
                    strokeDasharray="100"
                    strokeDashoffset={100 - globalThreatLevel}
                    strokeLinecap="round"
                    transform="rotate(-90 18 18)">
                  </circle>
                </svg>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-lg sm:text-xl font-bold">{globalThreatLevel}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 1. AlertStrike Regional Portals */}
      <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center before:content-['//'] before:text-red-600 before:mr-2 after:content-[''] after:h-px after:flex-grow after:bg-gradient-to-r after:from-red-900/50 after:to-transparent after:ml-3">
        <Globe className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-600" />
        AlertStrike Regional Portals
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10">
        {/* Pakistan Portal - Enhanced Card */}
        <a 
          href="https://demo-pk.alertstrike.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="group relative overflow-hidden rounded-xl border border-slate-800 bg-gradient-to-br from-slate-950/90 to-black/95 backdrop-blur-sm transition-all duration-300 hover:border-red-700/50 hover:shadow-[0_0_40px_rgba(185,28,28,0.2)] hover:-translate-y-1"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 to-purple-900/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-900 via-red-600 to-red-900 opacity-30 group-hover:opacity-80 transition-opacity"></div>
          
          {/* Animated Corner Accent */}
          <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-10 h-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute top-0 right-0 h-10 w-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
          
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 sm:p-3 bg-black/70 rounded-lg border border-red-900/30 group-hover:border-red-700/60 transition-colors">
                  <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">AlertStrike PAK</h3>
                  <p className="text-red-400 text-xs sm:text-sm">Pakistan Security Operations</p>
                </div>
              </div>
              <div className="relative">
                <span className="bg-green-900/30 text-green-400 text-xs px-2 sm:px-3 py-1 rounded-full border border-green-900/30 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-xs sm:text-sm">Operational Status</span>
                <span className="text-green-400 text-xs sm:text-sm font-mono">OPTIMAL</span>
              </div>
              <div className="w-full h-2 bg-slate-800/70 rounded-full overflow-hidden">
                <div className="h-full w-[98%] bg-gradient-to-r from-red-900 via-red-600 to-red-700 rounded-full relative">
                  <div className="absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-transparent via-red-300/30 to-transparent animate-pulse"></div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1 text-red-500" />
                  Alerts
                </p>
                <p className="text-base sm:text-xl font-bold">23</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Network className="h-3 w-3 mr-1 text-blue-500" />
                  Assets Capacity
                </p>
                <p className="text-base sm:text-xl font-bold">2000</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-3 w-3 mr-1" />
                  Score
                </p>
                <p className="text-base sm:text-xl font-bold">94<span className="text-xs sm:text-sm font-normal">/100</span></p>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BarChart className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                <span className="text-xs sm:text-sm text-red-400 font-mono">Last: 00:03:21 ago</span>
              </div>
              <button className="flex items-center space-x-2 bg-red-900/40 hover:bg-red-800/60 backdrop-blur transition-all px-2 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm border border-red-700/40 group-hover:border-red-600/70 group-hover:shadow-[0_0_15px_rgba(185,28,28,0.3)]">
                <span>Access Portal</span>
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 transform group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </a>
        
        {/* US Portal - Enhanced Card */}
        <a 
          href="https://demo-us.alertstrike.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="group relative overflow-hidden rounded-xl border border-slate-800 bg-gradient-to-br from-slate-950/90 to-black/95 backdrop-blur-sm transition-all duration-300 hover:border-red-700/50 hover:shadow-[0_0_40px_rgba(185,28,28,0.2)] hover:-translate-y-1"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 to-purple-900/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-900 via-red-600 to-red-900 opacity-30 group-hover:opacity-80 transition-opacity"></div>
          
          {/* Animated Corner Accent */}
          <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-10 h-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute top-0 right-0 h-10 w-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
          
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 sm:p-3 bg-black/70 rounded-lg border border-red-900/30 group-hover:border-red-700/60 transition-colors">
                  <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">AlertStrike US</h3>
                  <p className="text-red-400 text-xs sm:text-sm">US Security Operations</p>
                </div>
              </div>
              <div className="relative">
                <span className="bg-green-900/30 text-green-400 text-xs px-2 sm:px-3 py-1 rounded-full border border-green-900/30 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-xs sm:text-sm">Operational Status</span>
                <span className="text-green-400 text-xs sm:text-sm font-mono">OPTIMAL</span>
              </div>
              <div className="w-full h-2 bg-slate-800/70 rounded-full overflow-hidden">
                <div className="h-full w-[99%] bg-gradient-to-r from-red-900 via-red-600 to-red-700 rounded-full relative">
                  <div className="absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-transparent via-red-300/30 to-transparent animate-pulse"></div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1 text-red-500" />
                  Alerts
                </p>
                <p className="text-base sm:text-xl font-bold">45</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Network className="h-3 w-3 mr-1 text-blue-500" />
                  Assets Capacity
                </p>
                <p className="text-base sm:text-xl font-bold">250</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-3 w-3 mr-1" />
                  Score
                </p>
                <p className="text-base sm:text-xl font-bold">97<span className="text-xs sm:text-sm font-normal">/100</span></p>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BarChart className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                <span className="text-xs sm:text-sm text-red-400 font-mono">Last: 00:01:07 ago</span>
              </div>
              <button className="flex items-center space-x-2 bg-red-900/40 hover:bg-red-800/60 backdrop-blur transition-all px-2 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm border border-red-700/40 group-hover:border-red-600/70 group-hover:shadow-[0_0_15px_rgba(185,28,28,0.3)]">
                <span>Access Portal</span>
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 transform group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </a>
      </div>

      {/* 2. ThreatEye AI - Web Application Security & Audit */}
      <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center before:content-['//'] before:text-red-600 before:mr-2 after:content-[''] after:h-px after:flex-grow after:bg-gradient-to-r after:from-red-900/50 after:to-transparent after:ml-3">
        <ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-600" />
        AI Web Application Security & Audit Platform
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:gap-6 mb-8 sm:mb-10">
        {/* ThreatEye AI Card */}
        <a
          href="https://demo.threateye.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative overflow-hidden rounded-xl border border-slate-800 bg-gradient-to-br from-slate-950/90 to-black/95 backdrop-blur-sm transition-all duration-300 hover:border-red-700/50 hover:shadow-[0_0_40px_rgba(185,28,28,0.2)] hover:-translate-y-1"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 to-purple-900/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-900 via-red-600 to-red-900 opacity-30 group-hover:opacity-80 transition-opacity"></div>

          {/* Animated Corner Accent */}
          <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-10 h-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute top-0 right-0 h-10 w-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>

          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 sm:p-3 bg-black/70 rounded-lg border border-red-900/30 group-hover:border-red-700/60 transition-colors">
                  <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/Threateye-logo.png" alt="ThreatEye AI" className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">ThreatEye AI</h3>
                  <p className="text-red-400 text-xs sm:text-sm">AI Web Application Security & Audit</p>
                </div>
              </div>
              <div className="relative">
                <span className="bg-green-900/30 text-green-400 text-xs px-2 sm:px-3 py-1 rounded-full border border-green-900/30 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>
            </div>

            <div className="mb-4 sm:mb-6">
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                Advanced AI-powered vulnerability assessment platform for comprehensive web application security testing.
                Delivers enterprise-grade security audits with intelligent automation, detailed vulnerability reports, and
                actionable remediation guidance to maintain the highest security standards.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Scan className="h-3 w-3 mr-1 text-blue-500" />
                  Active Scans
                </p>
                <p className="text-base sm:text-xl font-bold">2</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Search className="h-3 w-3 mr-1 text-orange-500" />
                  Findings
                </p>
                <p className="text-base sm:text-xl font-bold">{vulnerabilityFindings}</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <FolderOpen className="h-3 w-3 mr-1 text-green-500" />
                  Projects
                </p>
                <p className="text-base sm:text-xl font-bold">3</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                <span className="text-xs sm:text-sm text-red-400 font-mono">Last: 00:00:08 ago</span>
              </div>
              <button className="flex items-center space-x-2 bg-red-900/40 hover:bg-red-800/60 backdrop-blur transition-all px-2 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm border border-red-700/40 group-hover:border-red-600/70 group-hover:shadow-[0_0_15px_rgba(185,28,28,0.3)]">
                <span>Access Portal</span>
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 transform group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </a>
      </div>

      {/* 3. AlertStrike AI Chat Bot */}
      <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center before:content-['//'] before:text-red-600 before:mr-2 after:content-[''] after:h-px after:flex-grow after:bg-gradient-to-r after:from-red-900/50 after:to-transparent after:ml-3">
        <Bot className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-600" />
        AlertStrike AI Chat
      </h2>
      
      <div className="grid grid-cols-1 gap-4 sm:gap-6 mb-8 sm:mb-10">
        {/* AlertStrike AI Chat Bot Card */}
        <div 
          className="group relative overflow-hidden rounded-xl border border-slate-800 bg-gradient-to-br from-slate-950/90 to-black/95 backdrop-blur-sm transition-all duration-300 hover:border-red-700/50 hover:shadow-[0_0_40px_rgba(185,28,28,0.2)] hover:-translate-y-1 cursor-pointer"
          onClick={() => setShowAIModal(true)}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 to-purple-900/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-900 via-red-600 to-red-900 opacity-30 group-hover:opacity-80 transition-opacity"></div>
          
          {/* Animated Corner Accent */}
          <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-10 h-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute top-0 right-0 h-10 w-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
          
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 sm:p-3 bg-black/70 rounded-lg border border-red-900/30 group-hover:border-red-700/60 transition-colors">
                  <Bot className="h-6 w-6 sm:h-8 sm:w-8 text-red-500" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">AlertStrike AI Chat</h3>
                  <p className="text-red-400 text-xs sm:text-sm">AI-Powered Security Assistant</p>
                </div>
              </div>
              <div className="relative">
                <span className="bg-green-900/30 text-green-400 text-xs px-2 sm:px-3 py-1 rounded-full border border-green-900/30 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                Advanced AI-powered AlertStrike Chat System  platform that enables interactive conversations and
                real-time understanding of cybersecurity situations through natural human language. It goes beyond 
                simply analyzing logs—AlertStrike interprets them intelligently and recommends the most effective 
                cybersecurity actions based on the context of each alert. You can select your region to ensure 
                compliance with local data regulations while using the platform.
              </p>
            </div>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Bot className="h-3 w-3 mr-1 text-red-500" />
                  Z0RX AI Models
                </p>
                <p className="text-base sm:text-xl font-bold">2</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Globe className="h-3 w-3 mr-1 text-green-500" />
                  Regions
                </p>
                <p className="text-base sm:text-xl font-bold">US & PK</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Activity className="h-3 w-3 mr-1 text-blue-500" />
                  Status
                </p>
                <p className="text-base sm:text-xl font-bold">Online</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                <span className="text-xs sm:text-sm text-red-400 font-mono">Last: 00:00:15 ago</span>
              </div>
              <button className="flex items-center space-x-2 bg-red-900/40 hover:bg-red-800/60 backdrop-blur transition-all px-2 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm border border-red-700/40 group-hover:border-red-600/70 group-hover:shadow-[0_0_15px_rgba(185,28,28,0.3)]">
                <span>Launch Chat System</span>
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 transform group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 4. AlertStrike Incident Response and Dark Web Monitoring */}
      <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center before:content-['//'] before:text-red-600 before:mr-2 after:content-[''] after:h-px after:flex-grow after:bg-gradient-to-r after:from-red-900/50 after:to-transparent after:ml-3">
        <Eye className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-600" />
        AlertStrike Incident Response and Dark Web Monitoring
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10">
        {/* AI Intel Eye - Enhanced Card */}
        <a 
          href="http://172.17.4.179:4000" 
          target="_blank" 
          rel="noopener noreferrer"
          className="group relative overflow-hidden rounded-xl border border-slate-800 bg-gradient-to-br from-slate-950/90 to-black/95 backdrop-blur-sm transition-all duration-300 hover:border-red-700/50 hover:shadow-[0_0_40px_rgba(185,28,28,0.2)] hover:-translate-y-1"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 to-purple-900/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-900 via-red-600 to-red-900 opacity-30 group-hover:opacity-80 transition-opacity"></div>
          
          {/* Animated Corner Accent */}
          <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-10 h-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute top-0 right-0 h-10 w-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
          
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 sm:p-3 bg-black/70 rounded-lg border border-red-900/30 group-hover:border-red-700/60 transition-colors">
                  <img src="https://pub-47f890e60ca1453698471756a9133329.r2.dev/AI-Intel-Eye.png" alt="AI Intel Eye" className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">AI Intel Eye</h3>
                  <p className="text-red-400 text-xs sm:text-sm">Intelligence & Dark Web Monitoring</p>
                </div>
              </div>
              <div className="relative">
                <span className="bg-green-900/30 text-green-400 text-xs px-2 sm:px-3 py-1 rounded-full border border-green-900/30 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                Intelligence-based monitoring and dark web monitoring platform that focuses on accelerating daily workflows, 
                including news aggregation and analysis, IOC and email investigations, and the creation of threat detection rules.
              </p>
            </div>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Eye className="h-3 w-3 mr-1 text-purple-500" />
                  Intel
                </p>
                <p className="text-base sm:text-xl font-bold">247</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Globe className="h-3 w-3 mr-1 text-blue-500" />
                  Dark Web
                </p>
                <p className="text-base sm:text-xl font-bold">89</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1 text-orange-500" />
                  IOCs
                </p>
                <p className="text-base sm:text-xl font-bold">1.2k</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                <span className="text-xs sm:text-sm text-red-400 font-mono">Last: 00:02:15 ago</span>
              </div>
              <button className="flex items-center space-x-2 bg-red-900/40 hover:bg-red-800/60 backdrop-blur transition-all px-2 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm border border-red-700/40 group-hover:border-red-600/70 group-hover:shadow-[0_0_15px_rgba(185,28,28,0.3)]">
                <span>Access Portal</span>
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 transform group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </a>
        
        {/* X-Incident Response System - Enhanced Card */}
        <a 
          href="https://172.17.4.179" 
          target="_blank" 
          rel="noopener noreferrer"
          className="group relative overflow-hidden rounded-xl border border-slate-800 bg-gradient-to-br from-slate-950/90 to-black/95 backdrop-blur-sm transition-all duration-300 hover:border-red-700/50 hover:shadow-[0_0_40px_rgba(185,28,28,0.2)] hover:-translate-y-1"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 to-purple-900/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-900 via-red-600 to-red-900 opacity-30 group-hover:opacity-80 transition-opacity"></div>
          
          {/* Animated Corner Accent */}
          <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-10 h-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute top-0 right-0 h-10 w-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
          
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 sm:p-3 bg-black/70 rounded-lg border border-red-900/30 group-hover:border-red-700/60 transition-colors">
                  <img src="https://pub-47f890e60ca1453698471756a9133329.r2.dev/1.png" alt="X-Incident Response" className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">X-Incident Response</h3>
                  <p className="text-red-400 text-xs sm:text-sm">Incident Response Platform</p>
                </div>
              </div>
              <div className="relative">
                <span className="bg-green-900/30 text-green-400 text-xs px-2 sm:px-3 py-1 rounded-full border border-green-900/30 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                Digital platform designed for incident response analysts to share complex investigations at a technical level. 
                Streamlines collaboration and enhances incident response efficiency across security teams.
              </p>
            </div>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Terminal className="h-3 w-3 mr-1 text-green-500" />
                  Cases
                </p>
                <p className="text-base sm:text-xl font-bold">342</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Cpu className="h-3 w-3 mr-1 text-blue-500" />
                  Analysts
                </p>
                <p className="text-base sm:text-xl font-bold">8</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Layers className="h-3 w-3 mr-1 text-purple-500" />
                  Playbooks
                </p>
                <p className="text-base sm:text-xl font-bold">24</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                <span className="text-xs sm:text-sm text-red-400 font-mono">Last: 00:01:43 ago</span>
              </div>
              <button className="flex items-center space-x-2 bg-red-900/40 hover:bg-red-800/60 backdrop-blur transition-all px-2 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm border border-red-700/40 group-hover:border-red-600/70 group-hover:shadow-[0_0_15px_rgba(185,28,28,0.3)]">
                <span>Access Portal</span>
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 transform group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </a>
      </div>

            {/* AI Chat Bot Region Selection Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4">
          <div className="bg-slate-950 border border-slate-800 rounded-xl w-full max-w-md relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-900 via-red-600 to-red-900"></div>
            
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold flex items-center">
                  <Bot className="h-5 w-5 mr-2 text-red-500" />
                  Select AlertStrike AI Region
                </h2>
                <button
                  onClick={closeAIChatbot}
                  className="p-2 bg-black/60 rounded-lg border border-slate-800 hover:border-red-900/30 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              
              <p className="text-slate-300 text-sm mb-6">
                Choose your preferred regional for accessing the AI AlertStrike Chat System:
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => handleAIRegionSelect('US')}
                  className="w-full p-4 bg-black/40 border border-slate-800 rounded-lg hover:border-red-900/50 hover:bg-red-900/10 transition-all text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="p-2 bg-blue-900/20 rounded-lg border border-blue-900/30 mr-3">
                        <Globe className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white">AlertStrike AI US</h3>
                        <p className="text-xs text-slate-400">US-based deployment with AlertStrike Logs</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-red-400 transition-colors" />
                  </div>
                </button>
                
                <button
                  onClick={() => handleAIRegionSelect('PK')}
                  className="w-full p-4 bg-black/40 border border-slate-800 rounded-lg hover:border-red-900/50 hover:bg-red-900/10 transition-all text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="p-2 bg-green-900/20 rounded-lg border border-green-900/30 mr-3">
                        <Globe className="h-5 w-5 text-green-500" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white">AlertStrike AI PK</h3>
                        <p className="text-xs text-slate-400">Pakistan-based deployment with AlertStrike Logs</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-red-400 transition-colors" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* 5. US and PK SOC Monitoring */}
      <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center before:content-['//'] before:text-red-600 before:mr-2 after:content-[''] after:h-px after:flex-grow after:bg-gradient-to-r after:from-red-900/50 after:to-transparent after:ml-3">
        <BarChart className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-600" />
        US and PK SOC Monitoring
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-1 gap-4 sm:gap-6 mb-8 sm:mb-10">
        {/* SOC Monitoring - Enhanced Card */}
        <a 
          href="http://172.17.4.179:3000" 
          target="_blank" 
          rel="noopener noreferrer"
          className="group relative overflow-hidden rounded-xl border border-slate-800 bg-gradient-to-br from-slate-950/90 to-black/95 backdrop-blur-sm transition-all duration-300 hover:border-red-700/50 hover:shadow-[0_0_40px_rgba(185,28,28,0.2)] hover:-translate-y-1"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 to-purple-900/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-900 via-red-600 to-red-900 opacity-30 group-hover:opacity-80 transition-opacity"></div>
          
          {/* Animated Corner Accent */}
          <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-10 h-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute top-0 right-0 h-10 w-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
          
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 sm:p-3 bg-black/70 rounded-lg border border-red-900/30 group-hover:border-red-700/60 transition-colors">
                  <img src="https://pub-47f890e60ca1453698471756a9133329.r2.dev/Grafana.png" alt="Grafana SOC Monitoring" className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">SOC Monitoring</h3>
                  <p className="text-red-400 text-xs sm:text-sm">Monitoring US & PK SOC Matrix</p>
                </div>
              </div>
              <div className="relative">
                <span className="bg-green-900/30 text-green-400 text-xs px-2 sm:px-3 py-1 rounded-full border border-green-900/30 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                Comprehensive monitoring dashboard for US & PK Security Operations Centers. Provides real-time visibility 
                into SOC performance metrics, analyst activity, threat detection rates, and operational efficiency across 
                both regional security operations centers.
              </p>
            </div>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Server className="h-3 w-3 mr-1 text-blue-500" />
                  US SOC
                </p>
                <p className="text-base sm:text-xl font-bold">98%</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Server className="h-3 w-3 mr-1 text-green-500" />
                  PK SOC
                </p>
                <p className="text-base sm:text-xl font-bold">97%</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Activity className="h-3 w-3 mr-1 text-purple-500" />
                  Dashboards
                </p>
                <p className="text-base sm:text-xl font-bold">2</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <LineChart className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                <span className="text-xs sm:text-sm text-red-400 font-mono">Last: 00:00:45 ago</span>
              </div>
              <button className="flex items-center space-x-2 bg-red-900/40 hover:bg-red-800/60 backdrop-blur transition-all px-2 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm border border-red-700/40 group-hover:border-red-600/70 group-hover:shadow-[0_0_15px_rgba(185,28,28,0.3)]">
                <span>Access Portal</span>
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 transform group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </a>
      </div>
    </main>
  );
}

export default Dashboard;
